import {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    useNavigate,
} from "react-router-dom";

import DataTable from "components/table/DataTable";

import {
    getSampleIssues,
    cancelSampleIssue,
} from "services/api/sampleTrackingApi";

import {
    showSuccess,
    showError,
} from "utils/toast";

import {
    sampleTrackingColumns,
} from "./columns";

import SampleIssueModal from "./SampleIssueModal";


export default function SampleTracking() {

    const navigate =
        useNavigate();


    const [
        issues,
        setIssues,
    ] = useState([]);


    const [
        loading,
        setLoading,
    ] = useState(false);


    const [
        page,
        setPage,
    ] = useState(1);


    const [
        search,
        setSearch,
    ] = useState("");


    const [
        returnStatus,
        setReturnStatus,
    ] = useState("");


    const [
        conversionStatus,
        setConversionStatus,
    ] = useState("");


    const [
        openModal,
        setOpenModal,
    ] = useState(false);


    const [
        selectedItem,
        setSelectedItem,
    ] = useState(null);


    const [
        pagination,
        setPagination,
    ] = useState({
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
    });


    /*
    |--------------------------------------------------------------------------
    | BOOKS
    |--------------------------------------------------------------------------
    |
    | For now we expect books to come from your Books API.
    | We'll connect the actual Books CRUD once that module is ready.
    |
    |--------------------------------------------------------------------------
    */

    const [
        books,
        setBooks,
    ] = useState([]);


    /*
    |--------------------------------------------------------------------------
    | LOAD
    |--------------------------------------------------------------------------
    */

    const loadData =
        async () => {

            try {

                setLoading(
                    true
                );


                const response =
                    await getSampleIssues(
                        page,
                        10,
                        {
                            returnStatus:
                                returnStatus ||
                                undefined,

                            conversionStatus:
                                conversionStatus ||
                                undefined,
                        }
                    );


                const records =
                    response?.data?.sampleIssues ||
                    [];


                setIssues(
                    Array.isArray(
                        records
                    )
                        ? records
                        : []
                );


                setPagination(
                    response?.data?.pagination ||
                    {}
                );

            } catch (
                error
            ) {

                console.error(
                    error
                );


                setIssues(
                    []
                );


                showError(
                    error?.response?.data?.message ||
                    "Unable to load sample tracking."
                );

            } finally {

                setLoading(
                    false
                );

            }
        };


    useEffect(
        () => {

            loadData();

        },
        [
            page,
            returnStatus,
            conversionStatus,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | LOCAL SEARCH
    |--------------------------------------------------------------------------
    */

    const filteredIssues =
        useMemo(
            () => {

                const value =
                    search
                        .trim()
                        .toLowerCase();


                if (!value) {
                    return issues;
                }


                return issues.filter(
                    (item) => {

                        const school =
                            item?.school?.name
                                ?.toLowerCase() ||
                            "";

                        const employee =
                            item?.employee?.name
                                ?.toLowerCase() ||
                            "";

                        const book =
                            item?.book?.title
                                ?.toLowerCase() ||
                            "";

                        const isbn =
                            item?.book?.isbn
                                ?.toLowerCase() ||
                            "";


                        return (
                            school.includes(
                                value
                            ) ||
                            employee.includes(
                                value
                            ) ||
                            book.includes(
                                value
                            ) ||
                            isbn.includes(
                                value
                            )
                        );

                    }
                );

            },
            [
                issues,
                search,
            ]
        );


    /*
    |--------------------------------------------------------------------------
    | ADD
    |--------------------------------------------------------------------------
    */

    const handleAdd =
        () => {

            setSelectedItem(
                null
            );

            setOpenModal(
                true
            );

        };


    /*
    |--------------------------------------------------------------------------
    | EDIT
    |--------------------------------------------------------------------------
    */

    const handleEdit =
        (item) => {

            setSelectedItem(
                item
            );

            setOpenModal(
                true
            );

        };


    /*
    |--------------------------------------------------------------------------
    | VIEW
    |--------------------------------------------------------------------------
    */

    const handleView =
        (item) => {

            navigate(
                `/admin/sample-tracking/${item.id}`
            );

        };


    /*
    |--------------------------------------------------------------------------
    | CANCEL
    |--------------------------------------------------------------------------
    */

    const handleCancel =
        async (
            item
        ) => {

            if (
                !window.confirm(
                    "Cancel this sample issue?"
                )
            ) {
                return;
            }


            try {

                await cancelSampleIssue(
                    item.id
                );


                showSuccess(
                    "Sample issue cancelled successfully."
                );


                loadData();

            } catch (
                error
            ) {

                showError(
                    error?.response?.data?.message ||
                    "Unable to cancel sample issue."
                );

            }

        };


    /*
    |--------------------------------------------------------------------------
    | COLUMNS
    |--------------------------------------------------------------------------
    */

    const columns =
        useMemo(
            () =>
                sampleTrackingColumns({

                    onView:
                        handleView,

                    onEdit:
                        handleEdit,

                    onCancel:
                        handleCancel,

                }),
            []
        );


    /*
    |--------------------------------------------------------------------------
    | MODAL CLOSE
    |--------------------------------------------------------------------------
    */

    const closeModal =
        () => {

            setOpenModal(
                false
            );

            setSelectedItem(
                null
            );

        };


    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (

        <div
            className="
                mt-5
                space-y-5
            "
        >

            {/* HEADER */}

            <div
                className="
                    flex
                    flex-col
                    gap-4
                    rounded-2xl
                    bg-white
                    p-5
                    shadow-sm
                    md:flex-row
                    md:items-center
                    md:justify-between
                "
            >

                <div>

                    <h1
                        className="
                            text-2xl
                            font-bold
                            text-navy-700
                        "
                    >
                        Sample Tracking
                    </h1>

                    <p
                        className="
                            mt-1
                            text-sm
                            text-gray-500
                        "
                    >
                        Track sample books issued,
                        returned and converted.
                    </p>

                </div>


                <button
                    type="button"
                    onClick={
                        handleAdd
                    }
                    className="
                        rounded-xl
                        bg-brand-500
                        px-5
                        py-3
                        text-sm
                        font-semibold
                        text-white
                        hover:bg-brand-600
                    "
                >
                    + Issue Sample
                </button>

            </div>


            {/* FILTERS */}

            <div
                className="
                    grid
                    grid-cols-1
                    gap-4
                    rounded-2xl
                    bg-white
                    p-4
                    shadow-sm
                    md:grid-cols-3
                "
            >

                <input
                    type="text"
                    value={
                        search
                    }
                    onChange={
                        (e) =>
                            setSearch(
                                e.target.value
                            )
                    }
                    placeholder="
                        Search school,
                        salesperson,
                        book...
                    "
                    className="
                        rounded-xl
                        border
                        border-gray-300
                        px-4
                        py-3
                        text-sm
                        outline-none
                        focus:border-brand-500
                    "
                />


                <select
                    value={
                        returnStatus
                    }
                    onChange={
                        (e) => {

                            setReturnStatus(
                                e.target.value
                            );

                            setPage(
                                1
                            );

                        }
                    }
                    className="
                        rounded-xl
                        border
                        border-gray-300
                        bg-white
                        px-4
                        py-3
                        text-sm
                    "
                >

                    <option value="">
                        All Return Status
                    </option>

                    <option value="issued">
                        Issued
                    </option>

                    <option value="partially_returned">
                        Partially Returned
                    </option>

                    <option value="returned">
                        Returned
                    </option>

                    <option value="converted">
                        Converted
                    </option>

                    <option value="cancelled">
                        Cancelled
                    </option>

                </select>


                <select
                    value={
                        conversionStatus
                    }
                    onChange={
                        (e) => {

                            setConversionStatus(
                                e.target.value
                            );

                            setPage(
                                1
                            );

                        }
                    }
                    className="
                        rounded-xl
                        border
                        border-gray-300
                        bg-white
                        px-4
                        py-3
                        text-sm
                    "
                >

                    <option value="">
                        All Conversion Status
                    </option>

                    <option value="pending">
                        Pending
                    </option>

                    <option value="partial">
                        Partial
                    </option>

                    <option value="converted">
                        Converted
                    </option>

                    <option value="not_converted">
                        Not Converted
                    </option>

                </select>

            </div>


            {/* TABLE */}

            <DataTable
                title="Sample Issues"
                columns={
                    columns
                }
                data={
                    filteredIssues
                }
                loading={
                    loading
                }
                onAdd={
                    handleAdd
                }
                addButtonText="Issue Sample"
            />


            {/* MODAL */}

            <SampleIssueModal
                open={
                    openModal
                }

                onClose={
                    closeModal
                }

                reload={
                    loadData
                }

                item={
                    selectedItem
                }

                books={
                    books
                }
            />

        </div>
    );
}